import { Component, OnInit, signal } from "@angular/core";
import { ISidepanelTab } from "src/app/components/sidepanel-tabs/sidepanel-tabs.component";
import { ActivatedRoute } from "@angular/router";
import { Location } from "@angular/common";
import { Router } from "@angular/router";
import { FiltersService } from "src/app/services/filters/filters.service";
import { PageWithFiltersEnum } from "src/app/models/filters/pageWithFilters.enum";
import { AiActivityFiltersModel } from "src/app/models/filters/implementations/pageFilters/aiActivityFilters.model";
import { AiFindingsFiltersModel } from "src/app/models/filters/implementations/pageFilters/aiFindingsFilters.model";
import { FiltersQueryTransformer } from "src/app/services/filters/filtersQueryTransformer.ts/filtersQueryTransformer";
import { EnterpriseAiDetectionTabsEnum } from "./enterprise-ai-detection-tabs-enum";
import { FeatureFlagsService } from "../../../services/infra/featureFlags/feature-flags.service";
import { FeatureFlagTypes } from "src/app/models/featureFlag/featureFalgsTypes.enum";

@Component({
    selector: "app-enterprise-ai-detection-tabs-management",
    templateUrl: "./enterprise-ai-detection-tabs-management.component.html",
    styleUrls: ["./enterprise-ai-detection-tabs-management.component.scss"],
})
export class EnterpriseAiDetectionTabsManagementComponent implements OnInit {
    // tabs
    public tabs: ISidepanelTab[] = [
        {
            labelPath: "enterpriseAiDetectionPage.tabs.findings",
        },
        {
            labelPath: "enterpriseAiDetectionPage.tabs.activity",
        },
    ];

    public showPreventionMockTab = false; // Controls visibility of the prevention tab
    public isAccountAuthorizedToViewMockPrevention = false;
    public isAccountAuthorizedToViewDataInsights = false;
    public isAccountAuthorizedToViewDataInLineControl = false;

    public currentTabIndex = 0;
    public findingsSubtitlePath = "enterpriseAiDetectionPage.tabs.findingsSubtitle";
    public preventionSubtitlePath = "enterpriseAiDetectionPage.tabs.preventionSubtitle";
    public findingsTooltipLabelPath = "enterpriseAiDetectionPage.tabs.findingsTooltip";
    public activitySubtitlePath = "enterpriseAiDetectionPage.tabs.activitySubtitle";
    public activityTooltipLabelPath = "enterpriseAiDetectionPage.tabs.activityTooltip";
    public preventionTooltipLabelPath = "enterpriseAiDetectionPage.tabs.preventionTooltip";
    public inLineControlSubtitlePath = "enterpriseAiDetectionPage.tabs.inLineControlSubtitle";
    // loading behavior subject
    public isLoading = signal<boolean>(false);

    constructor(
        private _route: ActivatedRoute,
        private _featureFlagsService: FeatureFlagsService,
        private _location: Location,
        private _router: Router,
        private _filtersService: FiltersService,
    ) {}

    ngOnInit(): void {
        this.isAccountAuthorizedToViewMockPrevention = this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.AIRuntimePreventionMockTab);
        this.isAccountAuthorizedToViewDataInsights = this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.AIRuntimeDataInsightsTab);
        this.isAccountAuthorizedToViewDataInLineControl = this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.AIRuntimeInLineControlTab);

        // Subscribe to queryParamMap to get query parameters
        this._route.queryParamMap.subscribe((params) => {
            const tabParam = params.get("tab");
            const showPreventionParam = params.get("show-prevention");

            // Show the prevention tab if the showPrevention parameter is present or if the tab is "prevention"
            this.showPreventionMockTab =
                this.isAccountAuthorizedToViewMockPrevention &&
                (showPreventionParam === "true" || tabParam === EnterpriseAiDetectionTabsEnum.Prevention);

            // Update the tabs array based on the visibility flag
            this.tabs = [
                {
                    labelPath: "enterpriseAiDetectionPage.tabs.findings",
                },
                {
                    labelPath: "enterpriseAiDetectionPage.tabs.activity",
                },
                ...(this.showPreventionMockTab
                    ? [
                          {
                              labelPath: "enterpriseAiDetectionPage.tabs.prevention",
                          },
                      ]
                    : []),
                ...(this.isAccountAuthorizedToViewDataInsights && !this.showPreventionMockTab
                    ? [
                          {
                              labelPath: "enterpriseAiDetectionPage.tabs.dataInsights",
                          },
                      ]
                    : []),
                ...(this.isAccountAuthorizedToViewDataInLineControl
                    ? [
                          {
                              labelPath: "enterpriseAiDetectionPage.tabs.inLineControl",
                          },
                      ]
                    : []),
            ];

            switch (tabParam) {
                case EnterpriseAiDetectionTabsEnum.Activity:
                    this.onTabSelect(1);
                    break;
                case EnterpriseAiDetectionTabsEnum.Findings:
                    this.onTabSelect(0);
                    break;
                case EnterpriseAiDetectionTabsEnum.Prevention:
                    this.onTabSelect(2);
                    break;
                case EnterpriseAiDetectionTabsEnum.DataInsights:
                    this.onTabSelect(2);
                    break;
                case EnterpriseAiDetectionTabsEnum.inLineControl:
                    this.onTabSelect(3);
                    break;
            }
        });
    }

    public onTabSelect(tabIndex: number) {
        const currentUrl = this._location.path().split("?")[0]; // Get the URL without query parameters
        const queryParams = new URLSearchParams(this._location.path().split("?")[1]); // Get existing query parameters
        // Set the new value or add if it doesn't exist
        let tabName = EnterpriseAiDetectionTabsEnum.Findings;
        if (tabIndex == 1) {
            tabName = EnterpriseAiDetectionTabsEnum.Activity;
        } else if (tabIndex == 2) {
            if (this.showPreventionMockTab) {
                tabName = EnterpriseAiDetectionTabsEnum.Prevention;
            } else {
                tabName = EnterpriseAiDetectionTabsEnum.DataInsights;
            }
        } else if (tabIndex == 3) {
            tabName = EnterpriseAiDetectionTabsEnum.inLineControl;
        }
        queryParams.set("tab", tabName);

        // Preserve the showPrevention parameter if it exists
        if (this.showPreventionMockTab) {
            queryParams.set("show-prevention", "true");
        }

        // Replace the state with the updated query parameters
        this._location.replaceState(`${currentUrl}?${queryParams.toString()}`);
        this.currentTabIndex = tabIndex;
    }

    public onUserTabSelect(tabIndex: number) {
        const queryParams = new URLSearchParams(this._location.path().split("?")[1]); // Get existing query parameters
        const currentTab = queryParams.get("tab")?.toLowerCase();

        // Find the current tab index based on the actual tabs array
        const currentTabIndex = this.tabs.findIndex((tab) => {
            const tabLabel = tab.labelPath.split(".").pop()?.toLowerCase();
            return tabLabel === currentTab;
        });

        if (currentTabIndex !== tabIndex) {
            // Get the tab label for the selected index
            const selectedTab = this.tabs[tabIndex];
            const tabLabel = selectedTab.labelPath.split(".").pop()?.toLowerCase();

            // Map the tab label to the corresponding enum value
            let tab = EnterpriseAiDetectionTabsEnum.Findings;
            if (tabLabel === "activity") {
                tab = EnterpriseAiDetectionTabsEnum.Activity;
            } else if (tabLabel === "prevention") {
                tab = EnterpriseAiDetectionTabsEnum.Prevention;
            } else if (tabLabel === "datainsights") {
                tab = EnterpriseAiDetectionTabsEnum.DataInsights;
            } else if (tabLabel === "inlinecontrol") {
                tab = EnterpriseAiDetectionTabsEnum.inLineControl;
            }

            const selectedFilters =
                tab === EnterpriseAiDetectionTabsEnum.Activity
                    ? (this._filtersService.getFilters(PageWithFiltersEnum.AiActivity) as AiActivityFiltersModel).getSelectedFilters()
                    : (this._filtersService.getFilters(PageWithFiltersEnum.AiFindings) as AiFindingsFiltersModel).getSelectedFilters();
            const filtersQuery = FiltersQueryTransformer.transformToQuery(selectedFilters);

            const newQueryParams = new URLSearchParams();
            // Add the tab parameter first
            newQueryParams.set("tab", tab);

            // Add the showPrevention parameter if it exists
            if (this.showPreventionMockTab && this.isAccountAuthorizedToViewMockPrevention) {
                newQueryParams.set("show-prevention", "true");
            }

            // Add the q parameter last
            newQueryParams.set("q", filtersQuery);

            // Replace the state with the updated query parameters
            this._router.navigate([], {
                queryParams: Object.fromEntries(newQueryParams.entries()),
                queryParamsHandling: "merge",
            });
            this.currentTabIndex = tabIndex;
        }
    }
}
